import { create } from 'zustand';

export type BackgroundStyle = 'liquid' | 'ascii' | 'particles' | 'topology' | 'waveform' | 'geometric' | 'rain' | 'orbit' | 'cube3d' | 'blank';

export type FontFamily = 'Inter' | 'Roboto' | 'Open Sans' | 'Lato' | 'Poppins' | 'Montserrat' | 'Nunito' | 'Source Sans Pro' | 'Ubuntu' | 'Oxygen';

export type ThemeSettings = {
  backgroundColor: string;
  backgroundSecondaryColor: string;
  backgroundStyle: BackgroundStyle;
  backgroundBrightness: number;
  backgroundTransparency: number;
  containerTransparency: number;
  backgroundSaturation: number;
  backgroundScale: number;
  backgroundAnimationSpeed: number;
  topNavColor: string;
  sidebarColor: string;
  uiColor: string;
  textColor: string;
  fontFamily: FontFamily;
  containerColor900: string;
  containerColor800: string;
  containerColor700: string;
  containerColor600: string;
  containerColorsLocked: boolean;
};

interface UIState {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isTypesOpen: boolean;
  setIsTypesOpen: (open: boolean) => void;
  backgroundColor: string;
  setBackgroundColor: (color: string) => void;
  backgroundSecondaryColor: string;
  setBackgroundSecondaryColor: (color: string) => void;
  backgroundStyle: BackgroundStyle;
  setBackgroundStyle: (style: BackgroundStyle) => void;
  backgroundBrightness: number;
  setBackgroundBrightness: (brightness: number) => void;
  backgroundTransparency: number;
  setBackgroundTransparency: (transparency: number) => void;
  containerTransparency: number;
  setContainerTransparency: (transparency: number) => void;
  backgroundSaturation: number;
  setBackgroundSaturation: (saturation: number) => void;
  backgroundScale: number;
  setBackgroundScale: (scale: number) => void;
  backgroundAnimationSpeed: number;
  setBackgroundAnimationSpeed: (speed: number) => void;
  // UI Theme customizations
  topNavColor: string;
  setTopNavColor: (color: string) => void;
  sidebarColor: string;
  setSidebarColor: (color: string) => void;
  uiColor: string;
  setUIColor: (color: string) => void;
  textColor: string;
  setTextColor: (color: string) => void;
  fontFamily: FontFamily;
  setFontFamily: (font: FontFamily) => void;
  // Container colors
  containerColor900: string;
  setContainerColor900: (color: string) => void;
  containerColor800: string;
  setContainerColor800: (color: string) => void;
  containerColor700: string;
  setContainerColor700: (color: string) => void;
  containerColor600: string;
  setContainerColor600: (color: string) => void;
  containerColorsLocked: boolean;
  setContainerColorsLocked: (locked: boolean) => void;
  initThemeCSS: () => void;
  loadTheme: () => Promise<boolean>;
  saveTheme: () => Promise<boolean>;
}

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function to update CSS custom properties
const updateAccentColors = (hexColor: string) => {
  const rgb = hexToRgb(hexColor);
  if (rgb) {
    document.documentElement.style.setProperty('--accent-1', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    document.documentElement.style.setProperty('--accent-2', `${Math.max(0, rgb.r - 20)}, ${Math.max(0, rgb.g - 20)}, ${Math.max(0, rgb.b - 20)}`);
  }
};

// Helper function to update UI theme colors
const updateUIThemeColors = (topNavColor: string, sidebarColor: string, uiColor: string, textColor: string) => {
  const topNavRgb = hexToRgb(topNavColor);
  const sidebarRgb = hexToRgb(sidebarColor);
  const uiRgb = hexToRgb(uiColor);
  const textRgb = hexToRgb(textColor);

  if (topNavRgb) {
    document.documentElement.style.setProperty('--nav-bg-color', `${topNavRgb.r}, ${topNavRgb.g}, ${topNavRgb.b}`);
  }
  if (sidebarRgb) {
    document.documentElement.style.setProperty('--sidebar-bg-color', `${sidebarRgb.r}, ${sidebarRgb.g}, ${sidebarRgb.b}`);
  }
  if (uiRgb) {
    document.documentElement.style.setProperty('--ui-bg-color', `${uiRgb.r}, ${uiRgb.g}, ${uiRgb.b}`);
  }
  if (textRgb) {
    // Update primary text color
    document.documentElement.style.setProperty('--text-color', `${textRgb.r}, ${textRgb.g}, ${textRgb.b}`);

    // Calculate and update zinc color variants based on the primary text color
    // These are approximations of the zinc scale based on the primary color
    const zinc100 = [
      Math.min(255, textRgb.r + 10),
      Math.min(255, textRgb.g + 10),
      Math.min(255, textRgb.b + 10)
    ];
    const zinc200 = [
      Math.min(255, textRgb.r + 20),
      Math.min(255, textRgb.g + 20),
      Math.min(255, textRgb.b + 20)
    ];
    const zinc300 = [
      Math.min(255, textRgb.r + 30),
      Math.min(255, textRgb.g + 30),
      Math.min(255, textRgb.b + 30)
    ];
    const zinc400 = [
      Math.max(0, textRgb.r - 30),
      Math.max(0, textRgb.g - 30),
      Math.max(0, textRgb.b - 30)
    ];
    const zinc500 = [
      Math.max(0, textRgb.r - 60),
      Math.max(0, textRgb.g - 60),
      Math.max(0, textRgb.b - 60)
    ];

    document.documentElement.style.setProperty('--text-primary', `${textRgb.r}, ${textRgb.g}, ${textRgb.b}`);
    document.documentElement.style.setProperty('--text-zinc-100', `${zinc100[0]}, ${zinc100[1]}, ${zinc100[2]}`);
    document.documentElement.style.setProperty('--text-zinc-200', `${zinc200[0]}, ${zinc200[1]}, ${zinc200[2]}`);
    document.documentElement.style.setProperty('--text-zinc-300', `${zinc300[0]}, ${zinc300[1]}, ${zinc300[2]}`);
    document.documentElement.style.setProperty('--text-zinc-400', `${zinc400[0]}, ${zinc400[1]}, ${zinc400[2]}`);
    document.documentElement.style.setProperty('--text-zinc-500', `${zinc500[0]}, ${zinc500[1]}, ${zinc500[2]}`);
  }
};

// Helper function to update font family
const updateFontFamily = (fontFamily: FontFamily) => {
  document.documentElement.style.setProperty('--font-family', fontFamily);
};

// Helper function to update container colors
const updateContainerColors = (color900: string, color800: string, color700: string, color600: string) => {
  const color900Rgb = hexToRgb(color900);
  const color800Rgb = hexToRgb(color800);
  const color700Rgb = hexToRgb(color700);
  const color600Rgb = hexToRgb(color600);

  if (color900Rgb) {
    document.documentElement.style.setProperty('--container-bg-color-900', `${color900Rgb.r}, ${color900Rgb.g}, ${color900Rgb.b}`);
  }
  if (color800Rgb) {
    document.documentElement.style.setProperty('--container-bg-color-800', `${color800Rgb.r}, ${color800Rgb.g}, ${color800Rgb.b}`);
  }
  if (color700Rgb) {
    document.documentElement.style.setProperty('--container-bg-color-700', `${color700Rgb.r}, ${color700Rgb.g}, ${color700Rgb.b}`);
  }
  if (color600Rgb) {
    document.documentElement.style.setProperty('--container-bg-color-600', `${color600Rgb.r}, ${color600Rgb.g}, ${color600Rgb.b}`);
  }
};

const useUIStore = create<UIState>((set, get) => ({
  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),
  isTypesOpen: false,
  setIsTypesOpen: (open) => set({ isTypesOpen: open }),
  backgroundColor: '#4ab4ff',
  setBackgroundColor: (color) => {
    set({ backgroundColor: color });
    // Update CSS custom properties when background color changes
    updateAccentColors(color);
  },
  backgroundSecondaryColor: '#000000',
  setBackgroundSecondaryColor: (color) => set({ backgroundSecondaryColor: color }),
  backgroundStyle: 'liquid' as BackgroundStyle,
  setBackgroundStyle: (style) => set({ backgroundStyle: style }),
  backgroundBrightness: 1.8,
  setBackgroundBrightness: (brightness) => set({ backgroundBrightness: brightness }),
  backgroundTransparency: 0.9,
  setBackgroundTransparency: (transparency) => set({ backgroundTransparency: transparency }),
  containerTransparency: 0.6,
  setContainerTransparency: (transparency) => {
    set({ containerTransparency: transparency });
    document.documentElement.style.setProperty('--container-transparency', transparency.toString());
  },
  backgroundSaturation: 1.0,
  setBackgroundSaturation: (saturation) => set({ backgroundSaturation: saturation }),
  backgroundScale: 1.0,
  setBackgroundScale: (scale) => set({ backgroundScale: scale }),
  backgroundAnimationSpeed: 1.0,
  setBackgroundAnimationSpeed: (speed) => set({ backgroundAnimationSpeed: speed }),
  // UI Theme customizations
  topNavColor: '#1a1a1a',
  setTopNavColor: (color) => {
    set({ topNavColor: color });
    const state = get();
    updateUIThemeColors(color, state.sidebarColor, state.uiColor, state.textColor);
    // Update container colors if they're locked
    if (state.containerColorsLocked) {
      updateContainerColors(color, state.containerColor800, state.containerColor700, state.containerColor600);
    }
  },
  sidebarColor: '#1a1a1a',
  setSidebarColor: (color) => {
    set({ sidebarColor: color });
    const state = get();
    updateUIThemeColors(state.topNavColor, color, state.uiColor, state.textColor);
    // Update container colors if they're locked
    if (state.containerColorsLocked) {
      updateContainerColors(state.containerColor900, color, state.containerColor700, state.containerColor600);
    }
  },
  uiColor: '#1f1f1f',
  setUIColor: (color) => {
    set({ uiColor: color });
    const state = get();
    updateUIThemeColors(state.topNavColor, state.sidebarColor, color, state.textColor);
    // Update container colors if they're locked
    if (state.containerColorsLocked) {
      updateContainerColors(state.containerColor900, state.containerColor800, color, state.containerColor600);
    }
  },
  textColor: '#f9fafb',
  setTextColor: (color) => {
    set({ textColor: color });
    const state = get();
    updateUIThemeColors(state.topNavColor, state.sidebarColor, state.uiColor, color);
  },
  fontFamily: 'Inter' as FontFamily,
  setFontFamily: (font) => {
    set({ fontFamily: font });
    updateFontFamily(font);
  },
  // Container colors - default to match nav colors initially
  containerColor900: '#1a1a1a',
  setContainerColor900: (color) => {
    set({ containerColor900: color });
    const state = get();
    updateContainerColors(color, state.containerColor800, state.containerColor700, state.containerColor600);
  },
  containerColor800: '#1f1f1f',
  setContainerColor800: (color) => {
    set({ containerColor800: color });
    const state = get();
    updateContainerColors(state.containerColor900, color, state.containerColor700, state.containerColor600);
  },
  containerColor700: '#242424',
  setContainerColor700: (color) => {
    set({ containerColor700: color });
    const state = get();
    updateContainerColors(state.containerColor900, state.containerColor800, color, state.containerColor600);
  },
  containerColor600: '#a1a1aa',
  setContainerColor600: (color) => {
    set({ containerColor600: color });
    const state = get();
    updateContainerColors(state.containerColor900, state.containerColor800, state.containerColor700, color);
  },
  containerColorsLocked: true,
  setContainerColorsLocked: (locked) => set({ containerColorsLocked: locked }),
  initThemeCSS: () => {
    const state = get();
    updateAccentColors(state.backgroundColor);
    updateUIThemeColors(state.topNavColor, state.sidebarColor, state.uiColor, state.textColor);
    updateFontFamily(state.fontFamily);
    updateContainerColors(state.containerColor900, state.containerColor800, state.containerColor700, state.containerColor600);
    document.documentElement.style.setProperty('--container-transparency', state.containerTransparency.toString());
  },
  loadTheme: async () => {
    try {
      const response = await fetch('/api/me/settings');
      if (!response.ok) return false;
      const data = await response.json();
      const theme = data.settings?.theme as ThemeSettings | undefined;
      if (theme) {
        set({
          backgroundColor: theme.backgroundColor,
          backgroundSecondaryColor: theme.backgroundSecondaryColor,
          backgroundStyle: theme.backgroundStyle,
          backgroundBrightness: theme.backgroundBrightness,
          backgroundTransparency: theme.backgroundTransparency,
          containerTransparency: theme.containerTransparency,
          backgroundSaturation: theme.backgroundSaturation,
          backgroundScale: theme.backgroundScale,
          backgroundAnimationSpeed: theme.backgroundAnimationSpeed,
          topNavColor: theme.topNavColor,
          sidebarColor: theme.sidebarColor,
          uiColor: theme.uiColor,
          textColor: theme.textColor,
          fontFamily: theme.fontFamily,
          containerColor900: theme.containerColor900,
          containerColor800: theme.containerColor800,
          containerColor700: theme.containerColor700,
          containerColor600: theme.containerColor600,
          containerColorsLocked: theme.containerColorsLocked,
        });
        get().initThemeCSS();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },
  saveTheme: async () => {
    const state = get();
    const themeData: ThemeSettings = {
      backgroundColor: state.backgroundColor,
      backgroundSecondaryColor: state.backgroundSecondaryColor,
      backgroundStyle: state.backgroundStyle,
      backgroundBrightness: state.backgroundBrightness,
      backgroundTransparency: state.backgroundTransparency,
      containerTransparency: state.containerTransparency,
      backgroundSaturation: state.backgroundSaturation,
      backgroundScale: state.backgroundScale,
      backgroundAnimationSpeed: state.backgroundAnimationSpeed,
      topNavColor: state.topNavColor,
      sidebarColor: state.sidebarColor,
      uiColor: state.uiColor,
      textColor: state.textColor,
      fontFamily: state.fontFamily,
      containerColor900: state.containerColor900,
      containerColor800: state.containerColor800,
      containerColor700: state.containerColor700,
      containerColor600: state.containerColor600,
      containerColorsLocked: state.containerColorsLocked,
    };
    const response = await fetch('/api/me/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'theme',
        settings: themeData
      }),
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return true;
  },
}));

// Initialize container colors and transparency on store creation
updateContainerColors('#1a1a1a', '#1f1f1f', '#242424', '#a1a1aa');
document.documentElement.style.setProperty('--container-transparency', '0.6');

export default useUIStore;